import * as THREE from 'three';
import { terrainHeight } from '../engine/terrain';

// SHATTER debris: when a salvage prop (barrel/junk/crate) is destroyed, a burst of little coloured chunks flies
// out, tumbles, falls to the ground under gravity, settles, then shrinks away after a couple of seconds. A single
// pooled InstancedMesh (one draw call for ALL live debris); CPU physics over a fixed pool. Client-only + cheap:
// no shadows, early-out when nothing is live. Spawned from the salvage-break event so it's authority-driven.

const CAP = 320;          // max live chunks
const GRAVITY = 17;
const LIFE = 2.6;         // seconds a chunk lives
const FADE = 0.9;         // last seconds it shrinks to nothing

// A small body+accent palette per archetype (debris reads as bits of the prop). Approximate; exact shade not critical.
const SHATTER_COLORS: Record<string, number[]> = {
  barrelBlue: [0x2f6fd0, 0x244f7a], barrelRed: [0xc23b2c, 0x7a2018], barrelGrey: [0x9aa3ab, 0x5a626a], barrelDark: [0x33373c, 0x202327],
  crateLarge: [0x9a6630, 0x5e3e1c], crateMilitary: [0x55633a, 0x333d22], toolbox: [0xc23b2c, 0x33373f],
  junk1: [0x6f6f6f, 0x383838], junk2: [0x6f6f6f, 0x383838], junk3: [0x6f6f6f, 0x383838],
  junk4: [0x6f6f6f, 0x383838], junk5: [0x6f6f6f, 0x383838], junk6: [0x6f6f6f, 0x383838],
};
const FALLBACK = [0x8a8a8a, 0x2a2d31];

export class ShatterField {
  readonly mesh: THREE.InstancedMesh;
  private readonly px = new Float32Array(CAP); private readonly py = new Float32Array(CAP); private readonly pz = new Float32Array(CAP);
  private readonly vx = new Float32Array(CAP); private readonly vy = new Float32Array(CAP); private readonly vz = new Float32Array(CAP);
  private readonly rx = new Float32Array(CAP); private readonly ry = new Float32Array(CAP); private readonly rz = new Float32Array(CAP);
  private readonly ax = new Float32Array(CAP); private readonly ay = new Float32Array(CAP); private readonly az = new Float32Array(CAP);
  private readonly age = new Float32Array(CAP); private readonly scl = new Float32Array(CAP); private readonly gy = new Float32Array(CAP);
  private readonly alive = new Uint8Array(CAP);
  private liveCount = 0;
  private maxUsed = 0; // highest slot ever touched (so update + draw skip the untouched tail)
  private cursor = 0;
  private readonly m = new THREE.Matrix4();
  private readonly p = new THREE.Vector3();
  private readonly q = new THREE.Quaternion();
  private readonly e = new THREE.Euler();
  private readonly s = new THREE.Vector3();
  private readonly col = new THREE.Color();

  constructor() {
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const mat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.82, metalness: 0.08 });
    this.mesh = new THREE.InstancedMesh(geo, mat, CAP);
    this.mesh.castShadow = false; this.mesh.receiveShadow = false;
    this.mesh.frustumCulled = false; this.mesh.count = 0;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  }

  // Burst chunks from a broken prop at world (x,z), coloured for the archetype `id`.
  burst(x: number, z: number, id: string | undefined): void {
    const pal = (id && SHATTER_COLORS[id]) || FALLBACK;
    const count = id && id.startsWith('junk') ? 16 : 11;
    const ground = terrainHeight(x, z);
    for (let k = 0; k < count; k++) {
      const i = this.alloc(); if (i < 0) break;
      const ang = Math.random() * Math.PI * 2, sp = 1.4 + Math.random() * 2.0;
      this.px[i] = x + (Math.random() - 0.5) * 0.35; this.py[i] = ground + 0.25 + Math.random() * 0.6; this.pz[i] = z + (Math.random() - 0.5) * 0.35;
      this.vx[i] = Math.cos(ang) * sp; this.vy[i] = 2.6 + Math.random() * 2.8; this.vz[i] = Math.sin(ang) * sp;
      this.rx[i] = Math.random() * 6.28; this.ry[i] = Math.random() * 6.28; this.rz[i] = Math.random() * 6.28;
      this.ax[i] = (Math.random() - 0.5) * 11; this.ay[i] = (Math.random() - 0.5) * 11; this.az[i] = (Math.random() - 0.5) * 11;
      this.age[i] = 0; this.scl[i] = 0.07 + Math.random() * 0.11; this.gy[i] = ground; this.alive[i] = 1;
      this.col.setHex(pal[k % pal.length]);
      this.mesh.setColorAt(i, this.col);
    }
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  private alloc(): number {
    for (let t = 0; t < CAP; t++) {
      const i = (this.cursor + t) % CAP;
      if (!this.alive[i]) { this.cursor = (i + 1) % CAP; this.liveCount++; if (i >= this.maxUsed) this.maxUsed = i + 1; return i; }
    }
    return -1; // pool full
  }

  update(dt: number): void {
    if (this.liveCount === 0) { this.mesh.count = 0; return; }
    for (let i = 0; i < this.maxUsed; i++) {
      if (!this.alive[i]) { this.m.makeScale(0, 0, 0); this.mesh.setMatrixAt(i, this.m); continue; }
      const age = this.age[i] + dt; this.age[i] = age;
      if (age >= LIFE) { this.alive[i] = 0; this.liveCount--; this.m.makeScale(0, 0, 0); this.mesh.setMatrixAt(i, this.m); continue; }
      const rest = this.gy[i] + this.scl[i] * 0.5;
      if (this.py[i] > rest) {
        this.vy[i] -= GRAVITY * dt;
        this.px[i] += this.vx[i] * dt; this.py[i] += this.vy[i] * dt; this.pz[i] += this.vz[i] * dt;
        this.rx[i] += this.ax[i] * dt; this.ry[i] += this.ay[i] * dt; this.rz[i] += this.az[i] * dt;
        if (this.py[i] <= rest) { this.py[i] = rest; this.vx[i] = this.vy[i] = this.vz[i] = 0; } // settle
      }
      const fade = age > LIFE - FADE ? Math.max(0, (LIFE - age) / FADE) : 1;
      this.p.set(this.px[i], this.py[i], this.pz[i]);
      this.e.set(this.rx[i], this.ry[i], this.rz[i]); this.q.setFromEuler(this.e);
      this.s.setScalar(this.scl[i] * fade);
      this.m.compose(this.p, this.q, this.s);
      this.mesh.setMatrixAt(i, this.m);
    }
    this.mesh.count = this.maxUsed;
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
